import { useEffect, useState } from 'react'
import { faRobot } from '@fortawesome/pro-duotone-svg-icons'

import Icon from '../icon/Icon'
import { requestCompareVerdict } from '../../_data/renter-llm'
import { CompareVerdictResult } from '../../_types/quickpass-llm'
import {
  Panel,
  Eyebrow,
  Glyph,
  Headline,
  WinnerLine,
  Reasoning,
  ProsGrid,
  ProsColumn,
  ProsHeader,
  ProsList,
  ProsItem,
  SwingFactor,
  SkeletonLine,
  QuietText,
  ErrorText,
} from './styled'
import {
  buildPairKey,
  compactListing,
  readCache,
  winnerLabelFor,
  writeCache,
} from './helpers'
import { CompareVerdictPanelProps, CompareVerdictPanelState } from './types'

/**
 * AI verdict panel that mounts at the top of the compare modal. Calls
 * the compare-verdict LLM kind once per pair (cached 24h) and renders
 * a coach-style winner pick + per-side pros + swing-factor.
 *
 * @param {CompareVerdictPanelProps} props
 * @returns {JSX.Element}
 */
const CompareVerdictPanel = ({
  alpha,
  beta,
  preferences,
}: CompareVerdictPanelProps): JSX.Element => {
  const [state, setState] = useState<CompareVerdictPanelState>('loading')
  const [verdict, setVerdict] = useState<CompareVerdictResult | null>(null)
  const [errorMessage, setErrorMessage] = useState<string>('')

  useEffect(() => {
    let isMounted = true
    const pairKey = buildPairKey(alpha.id, beta.id)
    const cached = readCache(pairKey)

    if (cached !== null) {
      setVerdict(cached.result)
      setState('ready')
      return
    }

    setState('loading')
    setErrorMessage('')

    const run = async (): Promise<void> => {
      try {
        const response = await requestCompareVerdict({
          alpha: compactListing(alpha),
          beta: compactListing(beta),
          preferences,
        })

        if (isMounted === false) {
          return
        }

        if (response === null) {
          setState('offline')
          return
        }

        writeCache({
          pairKey,
          result: response,
          generatedAt: Date.now(),
        })
        setVerdict(response)
        setState('ready')
      } catch (error) {
        if (isMounted === false) {
          return
        }
        setState('error')
        setErrorMessage(
          error instanceof Error
            ? error.message
            : 'Could not draft a verdict for this pair.',
        )
      }
    }

    void run()

    return () => {
      isMounted = false
    }
  }, [alpha, beta, preferences])

  if (state === 'loading') {
    return (
      <Panel>
        <Eyebrow>
          <Glyph aria-hidden="true">
            <Icon icon={faRobot} />
          </Glyph>
          QuickPass coach
        </Eyebrow>
        <SkeletonLine />
        <SkeletonLine style={{ width: '85%' }} />
      </Panel>
    )
  }

  if (state === 'offline') {
    return (
      <Panel>
        <Eyebrow>
          <Glyph aria-hidden="true">
            <Icon icon={faRobot} />
          </Glyph>
          QuickPass coach
        </Eyebrow>
        <QuietText>
          Backend not configured — the AI verdict needs the LLM endpoint to be
          reachable.
        </QuietText>
      </Panel>
    )
  }

  if (state === 'error') {
    return (
      <Panel>
        <Eyebrow>
          <Glyph aria-hidden="true">
            <Icon icon={faRobot} />
          </Glyph>
          QuickPass coach
        </Eyebrow>
        <ErrorText>{errorMessage}</ErrorText>
      </Panel>
    )
  }

  if (verdict === null) {
    return (
      <Panel>
        <Eyebrow>
          <Glyph aria-hidden="true">
            <Icon icon={faRobot} />
          </Glyph>
          QuickPass coach
        </Eyebrow>
        <QuietText>No verdict available right now.</QuietText>
      </Panel>
    )
  }

  return (
    <Panel>
      <Eyebrow>
        <Glyph aria-hidden="true">
          <Icon icon={faRobot} />
        </Glyph>
        QuickPass coach
      </Eyebrow>
      <Headline>{verdict.headline}</Headline>
      <WinnerLine>
        Pick: {winnerLabelFor(verdict.winner, alpha.title, beta.title)}
      </WinnerLine>
      <Reasoning>{verdict.reasoning}</Reasoning>

      <ProsGrid>
        <ProsColumn>
          <ProsHeader>{alpha.title} wins on</ProsHeader>
          <ProsList>
            {verdict.alphaPros.length === 0 ? (
              <ProsItem>—</ProsItem>
            ) : (
              verdict.alphaPros.map((entry, index) => (
                <ProsItem key={`alpha-${index}`}>{entry}</ProsItem>
              ))
            )}
          </ProsList>
        </ProsColumn>

        <ProsColumn>
          <ProsHeader>{beta.title} wins on</ProsHeader>
          <ProsList>
            {verdict.betaPros.length === 0 ? (
              <ProsItem>—</ProsItem>
            ) : (
              verdict.betaPros.map((entry, index) => (
                <ProsItem key={`beta-${index}`}>{entry}</ProsItem>
              ))
            )}
          </ProsList>
        </ProsColumn>
      </ProsGrid>

      <SwingFactor>Swing factor: {verdict.swingFactor}</SwingFactor>
    </Panel>
  )
}

export default CompareVerdictPanel
